/**
 * NOTE: This file is intentionally duplicated between frontend and backoffice.
 * Any changes here should be manually synced to the other location.
 *
 * Locations:
 * - frontend/src/services/api.ts
 * - backoffice/src/services/api.ts
 *
 * Consider extracting to shared package in the future.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

// Event emitter for authentication events (e.g., session expiration)
type AuthEventCallback = () => void
const authEventListeners: Set<AuthEventCallback> = new Set()

export const authEvents = {
  onUnauthorized(callback: AuthEventCallback): () => void {
    authEventListeners.add(callback)
    // Return unsubscribe function
    return () => authEventListeners.delete(callback)
  },
  emitUnauthorized(): void {
    authEventListeners.forEach(callback => callback())
  }
}

class ApiService {
  private baseUrl: string
  private csrfToken: string | null = null

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  /**
   * Fetch CSRF token from the server
   */
  async fetchCsrfToken(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/csrf-token`, {
        credentials: 'include',
      })
      const data = await response.json()
      this.csrfToken = data.token
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error)
    }
  }

  /**
   * Get the current CSRF token
   */
  getCsrfToken(): string | null {
    return this.csrfToken
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    }

    // Add CSRF token for state-changing methods
    const method = options.method?.toUpperCase()
    if (this.csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method || '')) {
      headers['X-CSRF-Token'] = this.csrfToken
    }

    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'include',
    }

    try {
      const response = await fetch(url, config)
      const data: ApiResponse<T> = await response.json()

      // Detect session expiration (401 Unauthorized)
      if (response.status === 401) {
        authEvents.emitUnauthorized()
        throw new Error(data.error || 'Session expirée')
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Une erreur est survenue')
      }

      return data.data as T
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Erreur réseau')
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async post<T, D = unknown>(endpoint: string, body: D): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async put<T, D = unknown>(endpoint: string, body: D): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  async uploadFile<T>(endpoint: string, file: File, additionalData?: Record<string, unknown>): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value))
      })
    }

    const url = `${this.baseUrl}${endpoint}`

    const headers: Record<string, string> = {}

    // Add CSRF token for POST requests
    if (this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      })

      const data: ApiResponse<T> = await response.json()

      // Detect session expiration (401 Unauthorized)
      if (response.status === 401) {
        authEvents.emitUnauthorized()
        throw new Error(data.error || 'Session expirée')
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Upload failed')
      }

      return data.data as T
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Upload error')
    }
  }
}

const api = new ApiService(API_URL)
export default api
